import { useState, useEffect } from 'react';
import { supabase, TABLES } from '../utils/supabase';
import type { TokenData } from '../types';

// Database-wide search implementation for token explore page

export const useSupabaseTokens = (externalSearchQuery?: string, categoryFilter?: string) => {
  const [tokens, setTokens] = useState<TokenData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTokens, setTotalTokens] = useState(0);
  const tokensPerPage = 18;

  const CATEGORY_VIEWS: Record<string, string> = {
    'Meme': 'tokens_meme',
    'DeFi': 'tokens_defi',
    'Utility': 'tokens_utility',
    'Gaming & Metaverse': 'tokens_gaming_metaverse',
    'SoFi': 'tokens_sofi',
    'Creator Coins': 'tokens_creator_coins',
    'AI & Tech': 'tokens_ai_tech',
    'DAO': 'tokens_dao',
    'Experimental/Art': 'tokens_experimental_art',
    'Kraken Girls': 'tokens_krakengirls',
  };

  const fetchTokens = async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const effectiveQuery = externalSearchQuery !== undefined ? externalSearchQuery : searchQuery;
      
      console.log('🔍 [useSupabaseTokens] Fetching tokens:', { 
        page, 
        effectiveQuery, 
        externalSearchQuery, 
        searchQuery 
      });
      
      // Branch 1: Category-first querying via views when a category is selected (no text search)
      const selectedView = categoryFilter && categoryFilter !== 'all' ? CATEGORY_VIEWS[categoryFilter] : undefined;

      if (!effectiveQuery && selectedView) {
        // Pull latest from the category view only
        const maxTokensToDisplay = 100;
        const { data, error } = await supabase
          .from(selectedView)
          .select('*')
          .eq('network', 'ink')
          .order('created_at', { ascending: false })
          .limit(maxTokensToDisplay);

        if (error) throw new Error(error.message);

        const totalMerged = (data || []).length;
        const calculatedPages = Math.ceil(totalMerged / tokensPerPage) || 1;
        setTotalTokens(totalMerged);
        setTotalPages(calculatedPages);

        const from = (page - 1) * tokensPerPage;
        const to = from + tokensPerPage;
        const paginated = (data || []).slice(from, to);
        setTokens(paginated as any);
        setCurrentPage(page);
        return;
      }

      if (effectiveQuery && effectiveQuery.trim()) {
        // If there's a search query, search BOTH tables and merge results
        const searchTerm = effectiveQuery.toLowerCase().trim();
        
        const [relayResult, velodromeResult] = await Promise.all([
          supabase
            .from(TABLES.TOKENS)
            .select('*')
            .eq('network', 'ink')
            .or(`name.ilike.%${searchTerm}%,symbol.ilike.%${searchTerm}%,contract_address.ilike.%${searchTerm}%`)
            .order('created_at', { ascending: false })
            .limit(50),
          supabase
            .from('tokens_velodrome')
            .select('*')
            .eq('network', 'ink')
            .or(`name.ilike.%${searchTerm}%,symbol.ilike.%${searchTerm}%,contract_address.ilike.%${searchTerm}%`)
            .order('created_at', { ascending: false })
            .limit(50)
        ]);

        if (relayResult.error) {
          throw new Error(relayResult.error.message);
        }
        if (velodromeResult.error) {
          throw new Error(velodromeResult.error.message);
        }

        // Merge and sort results by created_at
        const mergedTokens = [...(relayResult.data || []), ...(velodromeResult.data || [])]
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
          .slice(0, 100);

        setTokens(mergedTokens);
        setTotalTokens(mergedTokens.length);
        setTotalPages(1); // Search results don't use pagination
        setCurrentPage(1);
      } else {
        // No search query, use normal pagination
        // Fetch 100 from EACH table, then take top 100 after sorting
        // This ensures we get 100 tokens even if one table has fewer
        const maxTokensToFetch = 100; // Fetch 100 from each table
        const maxTokensToDisplay = 100; // Display only top 100 after merge
        
        // Fetch from BOTH tables (100 each)
        const [relayResult, velodromeResult] = await Promise.all([
          supabase
            .from(TABLES.TOKENS)
            .select('*')
            .eq('network', 'ink')
            .order('created_at', { ascending: false })
            .limit(maxTokensToFetch),
          supabase
            .from('tokens_velodrome')
            .select('*')
            .eq('network', 'ink')
            .order('created_at', { ascending: false })
            .limit(maxTokensToFetch)
        ]);

        if (relayResult.error) {
          throw new Error(relayResult.error.message);
        }
        if (velodromeResult.error) {
          throw new Error(velodromeResult.error.message);
        }

        // Merge and sort all tokens by created_at, then take top 100
        const allMergedTokens = [...(relayResult.data || []), ...(velodromeResult.data || [])]
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
          .slice(0, maxTokensToDisplay); // Limit to 100 most recent tokens

        // Set total based on limited results (100 tokens max)
        const totalMerged = allMergedTokens.length;
        const calculatedPages = Math.ceil(totalMerged / tokensPerPage);
        
        setTotalTokens(totalMerged);
        setTotalPages(calculatedPages);

        // Calculate pagination offsets
        const from = (page - 1) * tokensPerPage;
        const to = from + tokensPerPage - 1;

        console.log('🔍 [useSupabaseTokens] Fetching paginated tokens:', { 
          from, 
          to, 
          totalMerged,
          page,
          totalPages: calculatedPages
        });

        // Paginate the merged and sorted results
        const paginatedTokens = allMergedTokens.slice(from, to + 1);

        console.log('🔍 [useSupabaseTokens] Fetched tokens:', { 
          count: paginatedTokens.length, 
          sample: paginatedTokens.slice(0, 3).map(t => ({ 
            name: t.name, 
            deployer: t.deployer_address, 
            created: t.created_at 
          })) || []
        });

        setTokens(paginatedTokens);
        setCurrentPage(page);
        
        // Preloading disabled for merged results to avoid complexity
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch tokens');
    } finally {
      setLoading(false);
    }
  };

  // No need for client-side filtering since we now search at the database level
  const filteredTokens = tokens;

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      fetchTokens(page);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    fetchTokens(1);
  };

  useEffect(() => {
    fetchTokens(1);
  }, []);

  // Refetch when category filter changes
  useEffect(() => {
    fetchTokens(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryFilter]);

  // Trigger search when search query changes
  useEffect(() => {
    if (searchQuery !== '') {
      fetchTokens(1);
    }
  }, [searchQuery]);

  // Trigger search when external search query changes
  useEffect(() => {
    if (externalSearchQuery !== undefined) {
      fetchTokens(1);
    }
  }, [externalSearchQuery]);

  return {
    tokens: filteredTokens,
    allTokens: tokens,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    refetch: () => fetchTokens(currentPage),
    clearSearch,
    // Pagination
    currentPage,
    totalPages,
    totalTokens,
    tokensPerPage,
    goToPage,
    goToNextPage,
    goToPreviousPage,
  };
};

// Hook for creating a new token
export const useCreateToken = () => {
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createToken = async (
    tokenData: Omit<TokenData, 'id' | 'created_at' | 'updated_at'>,
    lpProvider: 'relay' | 'velodrome' = 'relay'
  ) => {
    try {
      setIsCreating(true);
      setError(null);

      // Select the correct table based on LP provider
      const tableName = lpProvider === 'velodrome' ? 'tokens_velodrome' : TABLES.TOKENS;
      
      const { data, error: insertError } = await supabase
        .from(tableName)
        .insert([tokenData])
        .select()
        .single();

      if (insertError) {
        throw new Error(insertError.message);
      }

      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create token';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  return {
    createToken,
    isCreating,
    error,
  };
};

// Hook for real-time token updates
export const useTokenSubscription = (onTokenUpdate?: (token: TokenData) => void) => {
  useEffect(() => {
    const subscription = supabase
      .channel('tokens_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: TABLES.TOKENS,
        },
        (payload) => {
          if (onTokenUpdate && payload.new) {
            onTokenUpdate(payload.new as TokenData);
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [onTokenUpdate]);
};
